import {
	Box,
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableRow,
} from '@mui/material';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CURRENCY, IS_SA } from '../../../../../../config/appConfig';
import { FinancialRatiosType } from '../../../../../../constants/financialRatiosTypes';
import { currencyFormatterHelper } from '../../../../../../helpers/currencyFormatterHelper';
import { DateHelper } from '../../../../../../helpers/dateHelper';
import { FormatNumberArray } from '../../../../../../helpers/formatNumberArray';
import { Section } from '../../../../../../shared/components/common/section';
import { CustomTooltip } from '../../../../../../shared/components/common/tooltip';
import { MarketplaceService } from '../../marketplaceService';

const zeroValues = ["0", "0.00M", "0.0M", "0M", "0.0%", "0.00"];

export const BusinessFinancialRatios = ({ businessDetails = null }) => {
	const marketplaceService = new MarketplaceService();
	const [financialRatios, setFinancialRatios] = useState([]);
	const [financialDetails, setFinancialDetails] = useState({});
	const [checkAllZero, setCheckAllZero] = useState(false);

	const { t } = useTranslation();

	useEffect(() => {
		if (businessDetails && businessDetails?.borrowerId) {
			loadFinancialRatios();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [businessDetails]);

	const formatValue = (value) => {
		let formattedValue = 0;
		if (value) {
			if (!isNaN(Number(value))) {
				formattedValue = !value.includes(',')
					? currencyFormatterHelper(value)
					: value;
			} else {
				formattedValue = value;
			}
		}
		return formattedValue;
	};

	const loadFinancialRatios = () => {
		marketplaceService
			.getFinancialRatios(businessDetails?.borrowerId)
			.then((resp) => {
				let respData = FormatNumberArray(resp.data?.ratios, ['periodEnding', 'type']);
				setFinancialDetails(resp.data);
				if (respData?.length) {
					let ratios = respData;
					ratios.forEach((ratio) => {
						Object.keys(ratio).forEach((key) => {
							if (key === 'periodEnding' || key === 'type') {
							} else {
								ratio[key] = formatValue(ratio[key]);
							}
						});
					});
					checkYearToDateEmpty(ratios);
				}
			})
			.catch((err) => { console.log(err); });
	};

	const checkYearToDateEmpty = (ratios = []) => {
		let ratiosValues = ratios;
		setCheckAllZero(checkRatiosValidity(ratiosValues));
		ratiosValues = ratiosValues.reverse();
		setFinancialRatios([...ratiosValues]);
	};

	const checkRatiosValidity = (ratios) => {


		return ratios.every(ratio => {
			return Object.keys(ratio).every(key => {
				if (["periodEnding", "type", "includesAuditedFigures"].includes(key)) {
					return true;
				}
				const value = ratio[key];
				return value === null || zeroValues.includes(value?.toString());
			});
		});
	};

	const checkFiledisEmpty = (key) => {
		return financialRatios.every(obj =>
			!obj[key] ||
			obj[key] === 0 ||
			obj[key] === "0" ||
			obj[key] === "0.00M" ||
			obj[key] === "0.0M" ||
			obj[key] === "0M" ||
			obj[key] === '0.0%' ||
			obj[key] === "0.00"
		);
	};

	const checkGrowth = () => {
		if (financialRatios && financialRatios.length && financialRatios?.length === 2) {
			return !!!financialRatios.find(resp => resp.type === 1);
		}
		return true;
	};

	const showColumns = (periodEnding) => {
		if (!periodEnding) {
			return false;
		}
		return financialRatios.find((resp) => resp?.periodEnding === periodEnding);
	};

	const checkIfDate = (period) => {
		const date = new Date(period);
		if (date && !isNaN(date?.getTime())) {
			return DateHelper.formatDate(date);
		}
		return period;
	};

	const formatNumbers = (value) => {
		if (!value) return 0;

		if (typeof value === 'number') {
			return value >= 0 ? currencyFormatterHelper(value) : 'N/A';
		}

		const numeric = parseFloat(value.replace(/[% ,]/g, ''));

		if (isNaN(numeric)) {
			return value;
		}
		return numeric >= 0 ? currencyFormatterHelper(value) : 'N/A';
	};

	const getRevenueGrowth = (value) => {
		if (!value && value !== 0) {
			return '0.0%';
		}

		if (zeroValues.includes(value?.toString())) {
			return 'N/A';
		}

		return currencyFormatterHelper(value);

	};

	return financialRatios?.length && !checkAllZero ? (
		<Box className="relative">
			<Section
				classList="!p-0"
				children={
					<Box>
						<Table>
							<TableHead className="shadow-md">
								<TableRow>
									<TableCell className="!font-bold">
										<Box className="flex flex-col">
											<Box>{t('financialHighlight')}</Box>
											{financialDetails?.type === FinancialRatiosType.Audited ||
												financialDetails?.type ===
												FinancialRatiosType.Internal ? (
												<Box className="!text-[12px] !font-[500] md:!mt-0 mt-1">
													{financialDetails?.type ===
														FinancialRatiosType.Audited
														? t('basedOnAudit')
														: t('basedOnInternal')}
												</Box>
											) : null}
										</Box>
									</TableCell>
									{financialRatios.map((ratio, index) => {
										return (
											<>
												{showColumns(ratio?.periodEnding) ? (
													<TableCell className="!font-bold">
														{checkIfDate(ratio?.periodEnding)}
													</TableCell>
												) : null}</>
										);
									})}
								</TableRow>
							</TableHead>
							<TableBody>
								{!checkFiledisEmpty("revenueBand") ? <TableRow>
									<TableCell>{<span>
										<span>{t('revenueBand')}</span> <span className='mx-0.5 currency-symbol'> ({CURRENCY})</span>
									</span>}</TableCell>
									{financialRatios.map((ratio, index) => {
										return (
											<TableCell key={index}>
												{currencyFormatterHelper(ratio?.revenueBand)}
											</TableCell>
										);
									})}
								</TableRow> : null}
								{!checkFiledisEmpty("netProfitBand") ? <TableRow>
									<TableCell>{<span>
										<span>{t('netProfitBand')}</span> <span className='mx-0.5 currency-symbol'> ({CURRENCY})</span>
									</span>}</TableCell>
									{financialRatios.map((ratio, index) => {
										return (
											<TableCell key={index}>
												{currencyFormatterHelper(ratio?.netProfitBand)}
											</TableCell>
										);
									})}
								</TableRow> : null}
								{!checkFiledisEmpty("revenueGrowth") && checkGrowth() ? <TableRow>
									<TableCell>{`${t('revenueGrowth')}`}</TableCell>
									{financialRatios.map((ratio, index) => {
										return (
											<TableCell key={index}>
												{getRevenueGrowth(ratio?.revenueGrowth)}
											</TableCell>
										);
									})}
								</TableRow> : null}
								{!checkFiledisEmpty("netProfitMargin") ? <TableRow>
									<TableCell>
										{`${t('netProfitMargin')}`}
										<CustomTooltip
											id="netProfitMargin"
											text={t('netProfitMarginHint')}></CustomTooltip>
									</TableCell>
									{financialRatios.map((ratio, index) => {
										return (
											<TableCell key={index}>
												{currencyFormatterHelper(ratio?.netProfitMargin)}
											</TableCell>
										);
									})}
								</TableRow> : null}
								{!checkFiledisEmpty("returnOnEquity") ? <TableRow>
									<TableCell>
										{`${t('returnOnEquity')}`}
										<CustomTooltip
											id={'returnOnEquity'}
											text={t('returnOnEquityHint')}></CustomTooltip>
									</TableCell>
									{financialRatios.map((ratio, index) => {
										return (
											<TableCell key={index}>
												{currencyFormatterHelper(ratio?.returnOnEquity)}
											</TableCell>
										);
									})}
								</TableRow> : null}
								{!checkFiledisEmpty("returnOnAssets") ? <TableRow>
									<TableCell>
										{`${t('returnOnAssets')}`}
										<CustomTooltip
											id="returnOnAssets"
											text={t('returnOnAssetsHint')}></CustomTooltip>
									</TableCell>
									{financialRatios.map((ratio, index) => {
										return (
											<TableCell key={index}>
												{currencyFormatterHelper(ratio?.returnOnAssets)}
											</TableCell>
										);
									})}
								</TableRow> : null}
								{!checkFiledisEmpty("totalOutstandingDebt") ? <TableRow>
									<TableCell>{t('totalOutstandingDebtFinance')}</TableCell>
									{financialRatios.map((ratio, index) => {
										return (
											<TableCell key={index}>
												{currencyFormatterHelper(ratio?.totalOutstandingDebt)}
											</TableCell>
										);
									})}
								</TableRow> : null}
								{!checkFiledisEmpty("inventoryDays") ? <TableRow>
									<TableCell>
										{t('inventoryDays')}
										<CustomTooltip
											text={t('inventoryDaysHint')}
											id="inventoryDays"></CustomTooltip>
									</TableCell>
									{financialRatios.map((ratio, index) => {
										return (
											<TableCell key={index}>{ratio?.inventoryDays}</TableCell>
										);
									})}
								</TableRow> : null}
								{!checkFiledisEmpty("debtorDays") ? <TableRow>
									<TableCell>
										{t('debtorDays')}{' '}
										<CustomTooltip
											text={t('debtorDaysHint')}
											id="debtorDays"></CustomTooltip>
									</TableCell>
									{financialRatios.map((ratio, index) => {
										return (
											<TableCell key={index}>{ratio?.debtorDays}</TableCell>
										);
									})}
								</TableRow> : null}
								{!checkFiledisEmpty("creditorDays") ? <TableRow>
									<TableCell>
										{t('creditorDays')}{' '}
										<CustomTooltip
											text={t('creditorDaysHint')}
											id="creditorDays"></CustomTooltip>
									</TableCell>
									{financialRatios.map((ratio, index) => {
										return (
											<TableCell key={index}>{ratio?.creditorDays}</TableCell>
										);
									})}
								</TableRow> : null}
								{!checkFiledisEmpty("levelOfExternalDebt") ? <TableRow>
									<TableCell>
										<span>
											<span>{t('levelofExternalDebt')}</span> <span className='mx-0.5 currency-symbol'> ({CURRENCY})</span>
										</span>
										<CustomTooltip
											text={t('levelofExternalDebtHint')}
											id="levelOfExternalDebt"></CustomTooltip>
									</TableCell>
									{financialRatios.map((ratio, index) => {
										return (
											<TableCell key={index}>
												{currencyFormatterHelper(ratio?.levelOfExternalDebt)}
											</TableCell>
										);
									})}
								</TableRow> : null}
								{!checkFiledisEmpty('debtRatio') ? <TableRow>
									<TableCell>
										{`${t('debtRatio')}`}{' '}
										<CustomTooltip
											text={t('debtRatioHint')}
											id="debtRatio"></CustomTooltip>
									</TableCell>
									{financialRatios.map((ratio, index) => {
										return (
											<TableCell key={index}>
												{formatNumbers(ratio?.debtRatio)}
											</TableCell>
										);
									})}
								</TableRow> : null}
								{!checkFiledisEmpty('interestCoverRatio') ? <TableRow>
									<TableCell>
										{t('interestCoverRatio')}{' '}
										<CustomTooltip
											text={t('interestCoverRatioHint')}
											id="interestCoverRatio"></CustomTooltip>
									</TableCell>
									{financialRatios.map((ratio, index) => {
										return (
											<TableCell key={index}>
												{formatNumbers(ratio?.interestCoverRatio)}
											</TableCell>
										);
									})}
								</TableRow> : null}
								{!checkFiledisEmpty('debtServiceCoverageRatio') ? <TableRow>
									<TableCell>
										{t('debtServiceCoverageRatio')}{' '}
										<CustomTooltip
											text={t('debtServiceCoverageRatioHint')}
											id="debtServiceCoverageRatio"></CustomTooltip>
									</TableCell>
									{financialRatios.map((ratio, index) => {
										return (
											<TableCell key={index}>
												{formatNumbers(
													ratio?.debtServiceCoverageRatio
												)}
											</TableCell>
										);
									})}
								</TableRow> : null}
								{!checkFiledisEmpty('debtToEquity') ? <TableRow>
									<TableCell>
										{t('debtToEquity')}{' '}
										<CustomTooltip
											text={t('debtToEquityHint')}
											id="debtToEquity"></CustomTooltip>
									</TableCell>
									{financialRatios.map((ratio, index) => {
										return (
											<TableCell key={index}>
												{formatNumbers(ratio?.debtToEquity)}
											</TableCell>
										);
									})}
								</TableRow> : null}
								{!checkFiledisEmpty('currentRatio') ? <TableRow>
									<TableCell>
										{t('currentRatio')}{' '}
										<CustomTooltip
											text={t('currentRatioHint')}
											id="currentRatio"></CustomTooltip>
									</TableCell>
									{financialRatios.map((ratio, index) => {
										return (
											<TableCell key={index}>
												{formatNumbers(ratio?.currentRatio)}
											</TableCell>
										);
									})}
								</TableRow> : null}
								{!checkFiledisEmpty('quickRatio') ? <TableRow>
									<TableCell>
										{t('quickRatio')}{' '}
										<CustomTooltip
											text={t('quickRatioHint')}
											id="quickRatio"></CustomTooltip>
									</TableCell>
									{financialRatios.map((ratio, index) => {
										return (
											<TableCell key={index}>
												{formatNumbers(ratio?.quickRatio)}
											</TableCell>
										);
									})}
								</TableRow> : null
								}
								{!checkFiledisEmpty('operatingCashflow') ? <TableRow>
									<TableCell>
										{t('operatingCashflow')}{' '}
										<CustomTooltip
											text={t('operatingCashflowHint')}
											id="operatingCashflow"></CustomTooltip>
									</TableCell>
									{financialRatios.map((ratio, index) => {
										return (
											<TableCell key={index}>
												{formatNumbers(ratio?.operatingCashflow)}
											</TableCell>
										);
									})}
								</TableRow> : null}
								{!checkFiledisEmpty('repaymentCoverageStrength') ? <TableRow>
									<TableCell>
										{t('repaymentCoverageStrength')}
										<CustomTooltip
											text={t('repaymentCoverageStrengthHint')}
											id="repaymentCoverageStrength"></CustomTooltip>
									</TableCell>
									{financialRatios.map((ratio, index) => {
										return (
											<TableCell key={index}>
												{ratio?.repaymentCoverageStrength}
											</TableCell>
										);
									})}
								</TableRow> : null}

							</TableBody>
						</Table>
					</Box>
				}></Section>
			<Box className="caption !text-[12px] mt-2 relative !top-[0.5rem]">
				{!IS_SA ? <span>
					<span>{t('limitsOnDueDiligence')}: </span>
					<span>{financialDetails?.limitsOnDueDiligence || t('notApplicable')}</span>
				</span> : null}
			</Box>

		</Box>
	) : null;
};
